import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Grid, Typography} from "@material-ui/core";
import {t} from "ttag";
import {AzoButton, TextInput} from "../../index";
import useLoginFormValidation from "./useLoginFormValidation";
import LoginFullscreenWrapper from './resources/LoginFullscreenWrapper';
import CircularProgress from "@material-ui/core/CircularProgress";
import useStyles from './LoginFormStyles';

const LoginForm = ({formData, formDataValidator, onChange, onSubmit, needValidation, disabled, helperText, fullscreen, submitting, fullscreenDialogProps}) => {

    const classes = useStyles();

    const [data, setData] = useState({...formData});
    const {validator, isFormDataValid} = useLoginFormValidation(formDataValidator);

    useEffect(() => {
        setData(formData)
    }, [setData, formData]);

    const handleDataChange = value => {
        const newData = ({...data, ...value});
        setData({...newData});
        onChange({...newData});
    };

    const canSubmit = !disabled && !submitting && isFormDataValid(data);

    const submitHandler = () => {
        if (!isFormDataValid(data)) {
            return setData({...data, needValidation: true});
        }

        onSubmit({...data});
    };

    const form = (
        <form>
            <Grid spacing={3} container>
                <Grid xs={12} sm={12} md={12} item>
                    <br />
                </Grid>
                <Grid xs={12} sm={12} md={12} item>
                    <Typography variant={'h4'} gutterBottom color={'primary'} align={'center'}>
                        {t`LOGIN`}
                    </Typography>
                </Grid>
                <Grid xs={12} sm={12} md={12} item>
                    <TextInput
                        label={t`Member ID or e-mail`}
                        value={data.username}
                        autoComplete={'username'}
                        onChange={({target: {value}}) => handleDataChange({username: value})}
                        error={needValidation && !!validator.username(data)}
                        helperText={needValidation && validator.username(data, validator)?.join(' ')}
                        disabled={disabled}
                        variant={'azo'}
                        autoFocus
                        fullWidth
                    />
                </Grid>
                <Grid xs={12} sm={12} md={12} item>
                    <TextInput
                        label={t`Password`}
                        type={'password'}
                        autoComplete={'current-password'}
                        value={data.password}
                        onKeyPress={e => e.key === 'Enter' && submitHandler()}
                        onChange={({target: {value}}) => handleDataChange({password: value})}
                        error={needValidation && !!validator.password(data)}
                        helperText={needValidation && validator.password(data, validator)?.join(' ')}
                        disabled={disabled}
                        variant={'azo'}
                        fullWidth
                    />
                </Grid>
                {helperText && (
                    <Grid xs={12} sm={12} md={12} item>
                        {helperText}
                    </Grid>
                )}
                <Grid xs={12} sm={12} md={12} item>
                    <AzoButton
                        onClick={submitHandler}
                        disabled={!canSubmit}
                        variant={'contained'}
                        color={'secondary'}
                        size={'medium'}
                        disableElevation
                        fullWidth>
                        {submitting ? <CircularProgress/> : <Typography variant={'h6'}>{t`Login`}</Typography>}
                    </AzoButton>
                </Grid>
                <Grid xs={12} sm={12} md={12} item>
                    <Typography className={classes.links} variant={'subtitle1'} gutterBottom color={'secondary'} align={'center'}>
                        <a href={'/user/password'}>{t`Reset your password`}</a>
                    </Typography>
                </Grid>
                <Grid xs={12} sm={12} md={12} item>
                    <Typography variant={'body1'} gutterBottom color={'primary'} align={'center'}>
                        {t`Join SATA IMAGINE and earn miles.`}
                    </Typography>
                    <Typography className={classes.links} variant={'subtitle1'} gutterBottom color={'secondary'} align={'center'}>
                        <a href={'/user/register'}>{t`Create new account`}</a>
                    </Typography>
                </Grid>
                <Grid xs={12} sm={12} md={12} item/>
            </Grid>
        </form>
    );

    if (fullscreen) {
        return (
            <LoginFullscreenWrapper {...fullscreenDialogProps}>
                {form}
            </LoginFullscreenWrapper>
        );
    }

    return form;
};

LoginForm.propTypes = {
    formData: PropTypes.shape({
        username: PropTypes.string,
        password: PropTypes.string,
    }),
    formDataValidator: PropTypes.object,
    onChange: PropTypes.func,
    onSubmit: PropTypes.func,
    needValidation: PropTypes.bool,
    disabled: PropTypes.bool,
    helperText: PropTypes.any,
    fullscreen: PropTypes.bool,
    submitting: PropTypes.bool,
    fullscreenDialogProps: PropTypes.object,
};

LoginForm.defaultProps = {
    formData: null,
    formDataValidator: {},
    onChange: () => null,
    onSubmit: () => null,
    needValidation: false,
    disabled: false,
    helperText: null,
    fullscreen: false,
    submitting: false,
    fullscreenDialogProps: {},
};

export default LoginForm;